/* Admin API client.
   Uses fetch with credentials so the httpOnly auth cookie is sent
   with every request. All routes prefixed with /api. */
const BASE = process.env.REACT_APP_BACKEND_URL;

const handle = async (res) => {
  let data = null;
  try {
    data = await res.json();
  } catch (e) {
    /* non-JSON response */
  }
  if (!res.ok) {
    const detail =
      (data && (data.detail || data.message)) || `HTTP ${res.status}`;
    const err = new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
    err.status = res.status;
    throw err;
  }
  return data;
};

const j = (method, path, body) =>
  fetch(`${BASE}${path}`, {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  }).then(handle);

export const api = {
  base: BASE,
  // Auth
  login: (email, password) => j("POST", "/api/auth/login", { email, password }),
  verify2fa: (ticket, code) => j("POST", "/api/auth/verify-2fa", { ticket, code }),
  resend2fa: (ticket) => j("POST", "/api/auth/resend-2fa", { ticket }),
  logout: () => j("POST", "/api/auth/logout"),
  me: () => j("GET", "/api/auth/me"),
  changePassword: (current_password, new_password) =>
    j("POST", "/api/auth/change-password", { current_password, new_password }),

  // Blog
  blogList: () => j("GET", "/api/blog"),
  blogGet: (slug) => j("GET", `/api/blog/${slug}`),
  blogCreate: (body) => j("POST", "/api/blog", body),
  blogUpdate: (slug, body) => j("PATCH", `/api/blog/${slug}`, body),
  blogDuplicate: (slug) => j("POST", `/api/blog/${slug}/duplicate`),
  blogDelete: (slug) => j("DELETE", `/api/blog/${slug}`),

  // Showcase
  showcaseList: () => j("GET", "/api/showcase"),
  showcaseGet: (slug) => j("GET", `/api/showcase/${slug}`),
  showcaseCreate: (body) => j("POST", "/api/showcase", body),
  showcaseUpdate: (slug, body) => j("PATCH", `/api/showcase/${slug}`, body),
  showcaseDuplicate: (slug) => j("POST", `/api/showcase/${slug}/duplicate`),
  showcaseDelete: (slug) => j("DELETE", `/api/showcase/${slug}`),

  // Settings
  settingsGet: () => j("GET", "/api/settings"),
  settingsUpdate: (body) => j("PATCH", "/api/settings", body),
  emailSettingsGet: () => j("GET", "/api/settings/email"),
  emailSettingsUpdate: (body) => j("PATCH", "/api/settings/email", body),

  // Scripts (Custom Scripts Manager)
  scriptsList: () => j("GET", "/api/scripts"),
  scriptCreate: (body) => j("POST", "/api/scripts", body),
  scriptUpdate: (sid, body) => j("PATCH", `/api/scripts/${sid}`, body),
  scriptDelete: (sid) => j("DELETE", `/api/scripts/${sid}`),

  // Public contact form
  submitContact: (body) => j("POST", "/api/contact", body),

  // SEO
  seoList: () => j("GET", "/api/seo"),
  seoByRoute: (route) =>
    j("GET", `/api/seo/by-route?route=${encodeURIComponent(route)}`),
  seoUpdate: (body, publish = false) =>
    j("PATCH", "/api/seo", { ...body, publish }),

  // Media
  mediaList: () => j("GET", "/api/media"),
  mediaUpload: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return fetch(`${BASE}/api/media`, {
      method: "POST",
      credentials: "include",
      body: fd,
    }).then(handle);
  },
  mediaUpdate: (id, body) => j("PATCH", `/api/media/${id}`, body),
  mediaDelete: (id) => j("DELETE", `/api/media/${id}`),

  // Preview → Production sync
  syncPush: () => j("POST", "/api/sync/push"),
  syncPushMedia: () => j("POST", "/api/sync/push-media"),
  syncCheckProduction: () => j("GET", "/api/sync/check-production"),

  // CMS backups (safety net)
  backupsList: () => j("GET", "/api/backups"),
  backupsCreate: () => j("POST", "/api/backups"),
  backupsRestore: (id) => j("POST", `/api/backups/${id}/restore`),
  backupsDelete: (id) => j("DELETE", `/api/backups/${id}`),

  // Page content
  contentList: () => j("GET", "/api/content"),
  contentGet: (key) => j("GET", `/api/content/${key}`),
  // Body may be either (a) the full CMS body object, or (b) a `data`
  // dict for backward compatibility. `publish` is optional.
  contentPut: (key, bodyOrData, publish) => {
    let body;
    if (
      bodyOrData &&
      typeof bodyOrData === "object" &&
      ("data" in bodyOrData || "route_path" in bodyOrData || "publish" in bodyOrData)
    ) {
      body = { ...bodyOrData };
      if (publish !== undefined) body.publish = publish;
    } else {
      body = { data: bodyOrData, publish: !!publish };
    }
    return j("PUT", `/api/content/${key}`, body);
  },
  pageRoutes: () => j("GET", "/api/page-routes"),
};
